/**
 * Phone Table Component
 *
 * Displays phone numbers in a table with sorting, selection, and actions.
 */

import { useNavigate } from 'react-router-dom';
import { cn, formatDate } from '@/utils';
import { Logger, LogCategory } from '@/utils/logger';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  TableLoading,
} from '@/components/ui/Table';
import { Checkbox } from '@/components/ui/Checkbox';
import { Button, IconButton } from '@/components/ui/Button';
import { FlagStatusBadge, ReputationSourcesList, Badge } from '@/components/ui/Badge';
import {
  usePhoneStore,
  usePaginatedPhones,
  usePhoneSortConfig,
  usePhoneLoading,
  useSelectedPhoneIds,
} from '@/stores/phoneStore';
import { useToast } from '@/components/ui/Toast';
import { ROUTES } from '@/constants';
import type { PhoneNumber, PhoneSortConfig } from '@/types';
import { useState, useRef, useEffect } from 'react';
import { Eye, MoreHorizontal, Archive, Phone } from 'lucide-react';

// ============================================================================
// Component
// ============================================================================

export function PhoneTable() {
  const navigate = useNavigate();
  const { success: showSuccess, error: showError } = useToast();
  const phones = usePaginatedPhones();
  const sortConfig = usePhoneSortConfig();
  const isLoading = usePhoneLoading();
  const selectedIds = useSelectedPhoneIds();
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const {
    setSortConfig,
    togglePhoneSelection,
    selectAllPhones,
    deselectAllPhones,
    archivePhone,
  } = usePhoneStore();

  // Close menu on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleArchive = async (phone: PhoneNumber) => {
    try {
      await archivePhone(phone.id);
      showSuccess('Archived', `${phone.formattedNumber} has been archived.`);
    } catch {
      showError('Error', 'Failed to archive phone number.');
    }
    setOpenMenuId(null);
  };

  const allSelected = phones.length > 0 && selectedIds.length === phones.length;
  const someSelected = selectedIds.length > 0 && selectedIds.length < phones.length;

  const handleSort = (field: PhoneSortConfig['field']) => {
    Logger.debug(LogCategory.UI, `Sort clicked: ${field}`, 'PhoneTable');
    const newDirection =
      sortConfig.field === field && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ field, direction: newDirection });
  };

  const handleSelectAll = () => {
    Logger.debug(LogCategory.UI, 'Select all clicked', 'PhoneTable');
    if (allSelected) {
      deselectAllPhones();
    } else {
      selectAllPhones();
    }
  };

  const handleRowClick = (phone: PhoneNumber) => {
    Logger.info(LogCategory.NAVIGATION, 'Navigating to phone details', 'PhoneTable', {
      phoneId: phone.id,
    });
    navigate(`${ROUTES.PHONE_NUMBERS}/${phone.id}`);
  };

  const getSortDirection = (field: PhoneSortConfig['field']): 'asc' | 'desc' | null => {
    return sortConfig.field === field ? sortConfig.direction : null;
  };

  // Get reputation score color
  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-success-600';
    if (score >= 50) return 'text-warning-600';
    return 'text-danger-600';
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={handleSelectAll}
                aria-label="Select all"
              />
            </TableHead>
            <TableHead
              sortable
              sorted={getSortDirection('number')}
              onSort={() => handleSort('number')}
            >
              Phone Number
            </TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Sources</TableHead>
            <TableHead
              sortable
              sorted={getSortDirection('reputationScore')}
              onSort={() => handleSort('reputationScore')}
            >
              Score
            </TableHead>
            <TableHead>Group</TableHead>
            <TableHead
              sortable
              sorted={getSortDirection('lastFlaggedDate')}
              onSort={() => handleSort('lastFlaggedDate')}
            >
              Last Flagged
            </TableHead>
            <TableHead className="w-20">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoading colSpan={8} rows={10} />
          ) : phones.length === 0 ? (
            <TableEmpty
              colSpan={8}
              icon={<Phone className="h-12 w-12" />}
              title="No phone numbers found"
              description="Try adjusting your filters or add new phone numbers."
              action={
                <Button variant="primary" size="sm">
                  Add Phone Numbers
                </Button>
              }
            />
          ) : (
            phones.map((phone) => (
              <TableRow
                key={phone.id}
                isSelected={selectedIds.includes(phone.id)}
                isClickable
                onClick={() => handleRowClick(phone)}
              >
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={selectedIds.includes(phone.id)}
                    onChange={() => togglePhoneSelection(phone.id)}
                    aria-label={`Select ${phone.formattedNumber}`}
                  />
                </TableCell>
                <TableCell>
                  <span className="font-mono font-medium text-slate-900">
                    {phone.formattedNumber}
                  </span>
                </TableCell>
                <TableCell>
                  <FlagStatusBadge status={phone.flagStatus} />
                </TableCell>
                <TableCell>
                  <ReputationSourcesList
                    sources={phone.reputationSources}
                    maxVisible={2}
                  />
                </TableCell>
                <TableCell>
                  <span className={cn('font-medium', getScoreColor(phone.reputationScore))}>
                    {phone.reputationScore}
                  </span>
                </TableCell>
                <TableCell>
                  {phone.callGroup ? (
                    <Badge variant="outline" size="sm">
                      {phone.callGroup}
                    </Badge>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell>
                  {phone.lastFlaggedDate ? (
                    <span className="text-slate-600">
                      {formatDate(phone.lastFlaggedDate, 'short')}
                    </span>
                  ) : (
                    <span className="text-slate-400">Never</span>
                  )}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center gap-1 relative">
                    <IconButton
                      icon={<Eye className="h-4 w-4" />}
                      variant="ghost"
                      size="sm"
                      aria-label="View details"
                      onClick={() => handleRowClick(phone)}
                    />
                    <div ref={openMenuId === phone.id ? menuRef : undefined}>
                      <IconButton
                        icon={<MoreHorizontal className="h-4 w-4" />}
                        variant="ghost"
                        size="sm"
                        aria-label="More actions"
                        onClick={() => setOpenMenuId(openMenuId === phone.id ? null : phone.id)}
                      />
                      {openMenuId === phone.id && (
                        <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-slate-200 rounded-lg shadow-lg z-10 py-1">
                          <button
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                            onClick={() => handleArchive(phone)}
                          >
                            <Archive className="h-4 w-4" />
                            Archive
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}
